import React, { useState } from 'react';
import { User } from '../../../types/auth';
import { Business, UserTierInfo } from '../../../types/accounting';
import { accountingApi } from '../../../services/accountingApi';
import { useCountry } from '../../../contexts/CountryContext';
import { todayLocalIso } from '../../../utils/inputFormatters';

interface BusinessSetupProps {
  user: User;
  userTier: UserTierInfo | null;
  hasSample: boolean;
  canCancel: boolean;
  onBusinessCreated: (business: Business) => void;
  onCancel: () => void;
}

const BusinessSetup: React.FC<BusinessSetupProps> = ({
  user,
  userTier,
  hasSample,
  canCancel,
  onBusinessCreated,
  onCancel
}) => {
  const { currencyCode } = useCountry();

  const [formData, setFormData] = useState({
    business_name: '',
    business_type: 'corporation' as const,
    business_start_date: todayLocalIso(),
    fiscal_year_end: `${new Date().getFullYear()}-12-31`,
    currency: currencyCode as 'CAD' | 'USD'
  });
  
  const [loading, setLoading] = useState(false);
  const [exploring, setExploring] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleExploreSample = async () => {
    try {
      setExploring(true);
      setError(null);
      const response = await accountingApi.createSampleBusiness();
      if (response.status === 'success' && response.data?.business) {
        onBusinessCreated(response.data.business);
      } else {
        setError(response.message || 'Failed to create the sample business');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to create the sample business');
    } finally {
      setExploring(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.business_name.trim()) {
      setError('Business name is required');
      return;
    }
    
    // Check if user can create another business
    if (userTier && userTier.usage) {
      if (userTier.usage.business_count >= userTier.limits.max_businesses) {
        setError(`You've reached your limit of ${userTier.limits.max_businesses} businesses. Upgrade to create more.`);
        return;
      }
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await accountingApi.createBusiness(formData);
      
      if (response.status === 'success' && response.data?.business) {
        onBusinessCreated(response.data.business);
      } else {
        setError(response.message || 'Failed to create business');
      }
      
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create business');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div style={{
      maxWidth: '600px',
      margin: '2rem auto',
      marginBottom: '4rem',
      padding: '2rem',
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgb(0 0 0 / 10%)'
    }}>
      <header style={{
        textAlign: 'center',
        marginBottom: '2rem'
      }}>
        <h2 style={{
          color: '#dc2626',
          fontSize: '1.5rem',
          margin: '0 0 0.5rem'
        }}>Get Started</h2>
        <p style={{
          color: '#6b7280',
          margin: 0
        }}>{hasSample ? 'Set up your own books.' : 'Try a sample business, or set up your own.'}</p>
      </header>

      {error && (
        <div className="error-message" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span>⚠️</span>
          {error}
        </div>
      )}

      {!hasSample && (
        <>
          <div className="explore-sample-card">
            <div>
              <strong>New here? Start with a business that already has books.</strong>
              <p>
                Maple Ridge Renovations is a small renovation contractor with three months of books.
                Post entries, send invoices, run reports. It's your own copy, remove it any time.
              </p>
            </div>
            <button
              type="button"
              className="btn-primary"
              onClick={handleExploreSample}
              disabled={exploring || loading}
            >
              {exploring ? 'Setting up your copy...' : 'Try the Sample Business'}
            </button>
          </div>

          <div className="setup-divider"><span>or set up your own</span></div>
        </>
      )}

      <form onSubmit={handleSubmit}>

        <div className="form-group">
          <label htmlFor="business_name">
            Business Name *
          </label>
          <input
            type="text"
            id="business_name"
            value={formData.business_name}
            onChange={(e) => handleChange('business_name', e.target.value)}
            placeholder="Enter your business name"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="business_type">
            Business Type
          </label>
          <select
            id="business_type"
            value={formData.business_type}
            onChange={(e) => handleChange('business_type', e.target.value)}
          >
            <option value="corporation">Corporation</option>
            <option value="sole_proprietorship">Sole Proprietorship</option>
            <option value="partnership">Partnership</option>
            <option value="llc">LLC</option>
          </select>
          <p style={{ color: '#6b7280', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>
            Currency: {currencyCode}
          </p>
        </div>

        <div className="business-form-row">
          <div className="form-group">
            <label htmlFor="business_start_date">
              Business Start Date
            </label>
            <input
              type="date"
              id="business_start_date"
              value={formData.business_start_date}
              onChange={(e) => handleChange('business_start_date', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label htmlFor="fiscal_year_end">
              Fiscal Year End
            </label>
            <input
              type="date"
              id="fiscal_year_end"
              value={formData.fiscal_year_end}
              onChange={(e) => handleChange('fiscal_year_end', e.target.value)}
            />
          </div>
        </div>

        <div style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'flex-end',
          marginTop: '2rem'
        }}>
          {canCancel && (
            <button
              type="button"
              className="btn-secondary"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            className="btn-primary"
            disabled={loading || exploring || !formData.business_name.trim()}
          >
            {loading ? 'Creating...' : 'Create Business'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BusinessSetup;